import { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';

/**
 * useDesignConfig — fetches the active company's Design Master config.
 *
 * The Design Number system (RD-101+) is a Super-Admin, per-company opt-in
 * (mirrors the Reference/Page-No feature). This hook tells any page whether
 * the feature is enabled for the given company and what the NEXT auto-assigned
 * design number would be (read-only preview, does NOT consume a number).
 *
 * Returns: { enabled, nextPreview, loading, refresh }
 */
export function useDesignConfig(companyId) {
  const [config, setConfig] = useState({ enabled: false, nextPreview: null });
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!companyId) {
      setConfig({ enabled: false, nextPreview: null });
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get('/designs/config', {
        params: { company_id: companyId },
      });
      setConfig({
        enabled: !!data?.enabled,
        nextPreview: data?.next_preview ?? null,
      });
    } catch {
      setConfig({ enabled: false, nextPreview: null });
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { refresh(); }, [refresh]);

  return {
    enabled: config.enabled,
    nextPreview: config.nextPreview,
    loading,
    refresh,
  };
}

export default useDesignConfig;
